import { getFirestore } from "firebase/firestore";
import { collection, doc, addDoc, onSnapshot } from "firebase/firestore";
import getStripe from "./initializeStripe";
import isUserPremium from "./isUserPremium";
import isEmbroideryPremium from "./isEmbroideryPremium";
import { newEventId, getMetaMatchData } from "../analytics/metaPixel";
import { getTikTokMatchData } from "../analytics/tiktokPixel";

// Prevent multiple simultaneous checkout sessions per user
const activeCheckoutSessions = new Map();

const PRICE_ID = {
  designer: process.env.REACT_APP_DESIGNER_PRICE_ID,
  designerYearly: process.env.REACT_APP_DESIGNER_YEARLY_PRICE_ID,
  monthly: process.env.REACT_APP_MONTHLY,
  yearly: process.env.REACT_APP_YEARLY,
  lifetime: process.env.REACT_APP_LIFETIME,
  credits5: process.env.REACT_APP_5_CREDITS,
  credits10: process.env.REACT_APP_10_CREDITS,
  credits15: process.env.REACT_APP_15_CREDITS,
  embroidery: process.env.REACT_APP_EMBROIDERY_PRICE_ID,
  embroideryYearly: process.env.REACT_APP_EMBROIDERY_YEARLY_PRICE_ID,
};

const EMBROIDERY_SUBSCRIPTION_TYPES = ["embroidery", "embroideryYearly"];

// Subscription types that should check for existing subscriptions
const SUBSCRIPTION_TYPES = ["monthly", "yearly", "designer", "designerYearly"];

export async function createCheckoutSession(uid, subscriptionType, options = {}) {
  // Prevent multiple simultaneous checkout sessions for the same user
  const sessionKey = `${uid}_${subscriptionType}`;
  if (activeCheckoutSessions.has(sessionKey)) {
    console.warn(`Checkout session creation already in progress for user ${uid} and subscription ${subscriptionType}. Ignoring duplicate request.`);
    return;
  }

  activeCheckoutSessions.set(sessionKey, Date.now());
  
  // Auto-cleanup after 5 minutes to prevent permanent locks
  setTimeout(() => {
    activeCheckoutSessions.delete(sessionKey);
  }, 5 * 60 * 1000);
  
  try {
    const firestore = getFirestore();
    const priceId = PRICE_ID[subscriptionType];
    
    // Check if price ID is configured
    if (!priceId) {
      console.error(`Price ID not found for subscription type: ${subscriptionType}`);
      console.error("Available price IDs:", PRICE_ID);
      activeCheckoutSessions.delete(sessionKey);
      window.dispatchEvent(new CustomEvent('stripe-checkout-error', { 
        detail: { error: `Subscription plan "${subscriptionType}" is not configured. Please contact support.` } 
      }));
      return;
    }

    // Check if user already has an active subscription (for subscription types only)
    if (SUBSCRIPTION_TYPES.includes(subscriptionType)) {
      const premiumStatus = await isUserPremium();
      if (premiumStatus && premiumStatus !== false) {
        console.warn(`User ${uid} already has premium status: ${premiumStatus}. Blocking duplicate subscription.`);
        activeCheckoutSessions.delete(sessionKey);
        window.dispatchEvent(new CustomEvent('stripe-checkout-error', { 
          detail: { error: 'You already have an active subscription. Please manage your subscription from your account settings.' } 
        }));
        return;
      }
    }

    if (EMBROIDERY_SUBSCRIPTION_TYPES.includes(subscriptionType)) {
      const embStatus = await isEmbroideryPremium();
      if (embStatus && embStatus !== false) {
        console.warn(`User ${uid} already has EmbroideryTrace membership: ${embStatus}. Blocking duplicate.`);
        activeCheckoutSessions.delete(sessionKey);
        window.dispatchEvent(new CustomEvent('stripe-checkout-error', {
          detail: { error: 'You already have an active EmbroideryTrace membership. Manage it from Billing.' }
        }));
        return;
      }
    }

    console.log(`Creating checkout session for ${subscriptionType} with price ID: ${priceId}`);

    // Store subscription type so the Purchase pixel event can read it after redirect
    localStorage.setItem("pendingCheckoutType", subscriptionType);
    if (options.coupon) {
      localStorage.setItem("pendingCheckoutCoupon", options.coupon);
    } else {
      localStorage.removeItem("pendingCheckoutCoupon");
    }

    // Generate a shared event_id for Meta Pixel + CAPI dedup.
    // We persist it in localStorage so the browser Purchase pixel can read it
    // after Stripe redirects back, and we pass it as metadata on the Stripe
    // Session (+ subscription/payment intent) so the server-side webhook uses
    // the exact same value when firing the CAPI Purchase event. Meta matches
    // the two and counts one purchase instead of two.
    const clientEventId = `purchase_${newEventId("chk")}`;
    localStorage.setItem("pendingCheckoutEventId", clientEventId);

    // Build success URL - redirect back to current page
    // Append checkout_success so the app can detect the return and poll for updated claims
    let successUrl = window.location.href;
    const successSeparator = successUrl.includes('?') ? '&' : '?';
    successUrl = `${successUrl}${successSeparator}checkout_success=true`;

    const pendingFileData = localStorage.getItem("pendingFile");
    if (pendingFileData) {
      try {
        const { fileId } = JSON.parse(pendingFileData);
        if (fileId) {
          successUrl = `${successUrl}&claimPending=${fileId}`;
        }
      } catch (e) {
        console.warn("Failed to parse pending file data:", e);
      }
    }

    const isSubscription = SUBSCRIPTION_TYPES.includes(subscriptionType) || EMBROIDERY_SUBSCRIPTION_TYPES.includes(subscriptionType);
    const skipTrial = EMBROIDERY_SUBSCRIPTION_TYPES.includes(subscriptionType) || options.trialFromPlan === false;

    // Tells the post-redirect hooks which Meta event this checkout warrants.
    // Only an immediate one-off payment can fire Purchase from the browser;
    // subscription revenue is reported by the webhook off the paid invoice.
    localStorage.setItem(
      "pendingCheckoutMode",
      !isSubscription ? "payment" : skipTrial ? "subscription" : "subscription_trial"
    );

    // Meta match signals, carried through Stripe so the webhook can attach them
    // to events fired after the browser is long gone (e.g. a trial converting).
    // Undefined entries are stripped because Firestore rejects undefined values.
    const metaMatchMetadata = Object.fromEntries(
      Object.entries(getMetaMatchData()).filter(([, value]) => value)
    );
    const tiktokMatchMetadata = Object.fromEntries(
      Object.entries(getTikTokMatchData()).filter(([, value]) => value)
    );

    const checkoutData = {
      price: priceId,
      success_url: successUrl,
      cancel_url: window.location.href,
      mode: isSubscription ? "subscription" : "payment",
      allow_promotion_codes: !options.coupon,
      // Stripe calculates expiry when the queued request is processed.
      // Session-level metadata — available on `session` in all webhook events
      metadata: {
        clientEventId,
        subscriptionType,
        ...metaMatchMetadata,
        ...tiktokMatchMetadata,
      },
      // Propagate metadata onto the resulting Subscription (for subscription mode)
      // so `customer.subscription.*` webhooks can read `subscription.metadata.clientEventId`.
      ...(isSubscription && {
        subscription_data: {
          metadata: {
            clientEventId,
            subscriptionType,
            ...metaMatchMetadata,
            ...tiktokMatchMetadata,
          },
        },
      }),
      // Propagate metadata onto the PaymentIntent (for one-time payment mode)
      // so `checkout.session.completed` / PaymentIntent webhooks can read it too.
      ...(!isSubscription && {
        payment_intent_data: {
          metadata: {
            clientEventId,
            subscriptionType,
            ...metaMatchMetadata,
            ...tiktokMatchMetadata,
          },
        },
      }),
      ...(isSubscription && {
        // EmbroideryTrace charges at checkout, even when the Stripe plan has a trial.
        trial_from_plan: !skipTrial,
      }),
      ...(options.coupon && {
        discounts: [{ coupon: options.coupon }],
      }),
    };

    const checkoutSessionRef = await addDoc(
      collection(doc(firestore, "users", uid), "checkout_sessions"),
      checkoutData
    );

    onSnapshot(checkoutSessionRef, async (snap) => {
      const data = snap.data();
      if (!data) return;
      
      const { sessionId, error } = data;
      
      // Check for errors from the Stripe extension
      if (error) {
        console.error("Stripe checkout error:", error);
        activeCheckoutSessions.delete(sessionKey);
        // Dispatch a custom event so the UI can handle the error
        window.dispatchEvent(new CustomEvent('stripe-checkout-error', { 
          detail: { error: error.message || 'Failed to create checkout session' } 
        }));
        return;
      }
      
      if (sessionId) {
        try {
          const stripe = await getStripe();
          stripe.redirectToCheckout({ sessionId });
        } catch (error) {
          console.error("Error redirecting to Stripe checkout:", error);
          window.dispatchEvent(new CustomEvent('stripe-checkout-error', { 
            detail: { error: 'Failed to redirect to checkout' } 
          }));
        } finally {
          // Reset the flag when redirecting to checkout or on error
          activeCheckoutSessions.delete(sessionKey);
        }
      }
    });
  } catch (error) {
    console.error("Error creating checkout session:", error);
    activeCheckoutSessions.delete(sessionKey);
    throw error;
  }
}
